import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  AddComplimentaryPeriodForm,
  AddComplimentaryPeriodFormValues,
} from 'src/components/AddComplimentaryPeriodModal/add-complimentary-period-form';
import { CppPayload } from 'src/components/AddComplimentaryPeriodModal/add-complimentary-period-modal';
import {
  getCppLengthList,
  TODAY_DATE,
} from 'src/components/AddComplimentaryPeriodModal/const';
import {
  AddPreSoldMembershipForm,
  AddPreSoldMembershipFormValues,
} from 'src/components/AddPreSoldMembershipModal/add-pre-sold-membership-form';
import { PsmPayload } from 'src/components/AddPreSoldMembershipModal/add-pre-sold-membership-modal';
import { preSoldLengths } from 'src/components/AddPreSoldMembershipModal/util';
import { DatePicker } from 'src/components/DatePicker';
import { RichTextField } from 'src/components/Forms/RichTextField/rich-text-field';
import {
  PartnerGroupRules,
  ProjectStages,
  useGroupRules,
  usePartnerGroupRules,
  useProjectStages,
  UserGroupRules,
  useSubmitDay1,
} from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { getRawTextFromHTML } from 'src/utils/text-parsing';
import { prepareSubmitDay1Data } from './util';

export interface SubmitDay1FormValues {
  projectId: string;
  day1Date: string;
  membershipSalesNotes?: string;
  cpp?: CppPayload;
  psm?: PsmPayload;
}

export const SubmitDay1Modal = () => {
  const { closeModal } = useModalMethods(Modals.SubmitDay1);
  const { isOpened, modalProps } = useModalState(Modals.SubmitDay1);
  const { partnerGroupRules } = usePartnerGroupRules(modalProps?.address?.partnerId);
  const { showSuccessAlert } = useNotifications();
  const hasAccessToPSM = partnerGroupRules[PartnerGroupRules.AccessToPresoldMemberships];
  const hasAccessToSubManagementPlus =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagementPlus];
  const { groupRules } = useGroupRules();

  const canUpdateSLA = Boolean(groupRules[UserGroupRules.CanUpdateSLAs]);
  const [showCppForm, setShowCppForm] = useState(!!canUpdateSLA);
  const [showPsmForm, setShowPsmForm] = useState(false);
  const [day1Date, setDay1Date] = useState<Dayjs | null>(
    dayjs(modalProps?.project?.estimatedDay1Date || TODAY_DATE),
  );
  const [membershipSalesNotes, setMembershipSalesNotes] = useState<string>(
    modalProps?.address?.membershipSalesNotes || '',
  );
  const [sendEducationalEmails, setSendEducationalEmails] = useState(false);

  const { projectStages } = useProjectStages({
    filters: [
      {
        attribute: 'projectStageId',
        operator: '=',
        value: ProjectStages.ServiceClient.toString(),
      },
    ],
    limit: 1,
  });

  const { submitDay1 } = useSubmitDay1();

  const hasScheduledPreSoldMembership = useMemo(() => {
    const psm = modalProps?.address?.preSoldMemberships?.[0];
    return psm?.isScheduled;
  }, [modalProps?.address?.preSoldMemberships]);

  const cppFormMethods = useForm<AddComplimentaryPeriodFormValues>({
    defaultValues: {
      partnerProductId: '',
      cppExpirationDate: getCppLengthList(day1Date)[2].value,
    },
  });

  const psmFormMethods = useForm<AddPreSoldMembershipFormValues>({
    defaultValues: {
      partnerProductId: '',
      price: 0,
      lengthInMonths: preSoldLengths[0].value,
    },
  });

  useEffect(() => {
    const psm = canUpdateSLA ? modalProps?.address?.preSoldMemberships?.[0] : false;

    if (psm && !psm.isScheduled) {
      setShowPsmForm(true);
    }

    if (modalProps?.project?.estimatedDay1Date) {
      setDay1Date(dayjs(modalProps.project.estimatedDay1Date));
    }
    if (modalProps?.address?.membershipSalesNotes) {
      setMembershipSalesNotes(modalProps.address.membershipSalesNotes);
    }
  }, [
    canUpdateSLA,
    modalProps?.address?.membershipSalesNotes,
    modalProps?.address?.preSoldMemberships,
    modalProps?.project?.estimatedDay1Date,
  ]);

  const handleClose = useCallback(() => {
    cppFormMethods.reset();
    psmFormMethods.reset();
    closeModal();
    setShowCppForm(true);
    setShowPsmForm(false);
    setSendEducationalEmails(false);
  }, [closeModal, cppFormMethods, psmFormMethods]);

  const onSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const project = modalProps?.project;
      const address = modalProps?.address;
      if ((!project?.id && !project?.projectId) || (!address?.id && !address?.addressId)) {
        return;
      }

      if (showCppForm) {
        const cppValid = await cppFormMethods.trigger();
        if (!cppValid) {
          return;
        }
      }
      if (showPsmForm) {
        const psmValid = await psmFormMethods.trigger();
        if (!psmValid) {
          return;
        }
      }

      const cppFormData = cppFormMethods.getValues();
      const psmFormData = psmFormMethods.getValues();

      const payload = prepareSubmitDay1Data({
        project,
        address,
        day1Date,
        membershipSalesNotes,
        sendEducationalEmails,
        showCppForm,
        showPsmForm,
        cppData: cppFormData,
        psmData: psmFormData,
      });

      handleClose();
      const result = await submitDay1(
        payload,
        {
          ...project,
          projectStage: projectStages?.[0],
        },
        address,
      );
      if (!result.errors) {
        showSuccessAlert('Day1 submitted successfully');
      }
    },
    [
      cppFormMethods,
      day1Date,
      handleClose,
      membershipSalesNotes,
      modalProps,
      sendEducationalEmails,
      projectStages,
      psmFormMethods,
      showCppForm,
      showPsmForm,
      showSuccessAlert,
      submitDay1,
    ],
  );

  const hasPermissionToSeePSM =
    hasAccessToPSM && !hasScheduledPreSoldMembership && canUpdateSLA;

  return (
    <Dialog open={isOpened} onClose={handleClose} fullWidth>
      <DialogTitle>
        <Typography>Submit Day 1</Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <DatePicker
            label="Day1"
            value={day1Date}
            onChange={setDay1Date}
            slotProps={{
              textField: {
                size: 'small',
              },
            }}
          />
          {!!hasAccessToSubManagementPlus && (
            <FormControlLabel
              sx={{ width: '100%' }}
              control={
                <Switch
                  onChange={() => setSendEducationalEmails(!sendEducationalEmails)}
                  checked={sendEducationalEmails}
                />
              }
              label="Send Educational Emails"
            />
          )}
          {!!canUpdateSLA && (
            <FormControlLabel
              sx={{ width: '100%' }}
              control={
                <Switch onChange={() => setShowCppForm(!showCppForm)} checked={showCppForm} />
              }
              label="Provide Complimentary Membership"
            />
          )}
          {showCppForm ? (
            <AddComplimentaryPeriodForm
              formMethods={cppFormMethods}
              address={modalProps?.address}
              baseDate={day1Date}
            />
          ) : null}

          {hasPermissionToSeePSM ? (
            <>
              <FormControlLabel
                sx={{ width: '100%', mt: 2 }}
                control={
                  <Switch
                    onChange={() => setShowPsmForm(!showPsmForm)}
                    checked={showPsmForm}
                  />
                }
                label="Add Pre-Sold Membership"
              />
              {showPsmForm ? (
                <AddPreSoldMembershipForm
                  formMethods={psmFormMethods}
                  address={modalProps?.address}
                />
              ) : null}
            </>
          ) : null}

          <Typography sx={{ mt: 2 }}>Membership Sales Notes</Typography>

          <RichTextField
            placeholder="Enter notes here"
            sx={{
              marginTop: 1,
            }}
            onBlur={(_, editor) => {
              const fieldContent = editor?.getHTML().trim() || '';
              const actualTextWithoutMarkup = getRawTextFromHTML(fieldContent);
              if (actualTextWithoutMarkup) {
                setMembershipSalesNotes(fieldContent);
              }
            }}
            editorOptions={{
              content: modalProps?.address?.membershipSalesNotes || membershipSalesNotes,
            }}
            onSubmit={(message) => setMembershipSalesNotes(message)}
          />

          <DialogActions>
            <Button onClick={onSubmit}>Submit</Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
